import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import BankDetailsTab from "./BankDetailsTab";
import BillerAddressTab from "./BillerAddressTab";
import CurrencyTab from "./CurrencyTab";
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from '../../../utils/makeRequest';
import {
    getBankDetailsURL,
    getBillerAddressesURL,
    GET_CURRENCIES_URL,
    finishOnboardingURL
} from '../../../utils/urls';
import { toastify } from "../../../utils/toast";

const Onboarding = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [completedSteps, setCompletedSteps] = useState([false, false, false]);
    const [skippedSteps, setSkippedSteps] = useState([false, false, false]);
    const [loading, setLoading] = useState(true);
    const [existingData, setExistingData] = useState({
        bankDetails: null,
        billerAddress: null,
        currency: null
    });

    const steps = [
        { id: 0, title: "Bank Details", icon: "💳" },
        { id: 1, title: "Biller Address", icon: "🏠" },
        { id: 2, title: "Currency", icon: "💰" }
    ];

    // Check for existing data on component mount
    useEffect(() => {
        checkExistingData();
    }, []);

    const checkExistingData = async () => {
        setLoading(true);
        try {
            const facilityId = await getItem('selectedFacilityId');

            if (!facilityId) {
                console.error('No facility ID found');
                setLoading(false);
                return;
            }

            // Check all four endpoints simultaneously
            const [bankResponse, billerResponse, currencyResponse, paymentResponse] = await Promise.allSettled([
                makeRequest2(`${getBankDetailsURL}/${facilityId}`, 'GET'),
                makeRequest2(`${getBillerAddressesURL}/${facilityId}`, 'GET'),
                makeRequest2(`${GET_CURRENCIES_URL}/${facilityId}`, 'GET'),
                makeRequest2(`/api/core/facility_payment_details/get_facility_payment_details/${facilityId}`, 'GET')
            ]);

            const newCompletedSteps = [false, false, false];
            const newExistingData = {
                bankDetails: null,
                billerAddress: null,
                currency: null,
                paymentDetails: null
            };

            // Check bank details - handle nested data structure
            if (bankResponse.status === 'fulfilled' &&
                bankResponse.value?.success &&
                bankResponse.value?.data?.success &&
                bankResponse.value?.data?.data &&
                Array.isArray(bankResponse.value.data.data) &&
                bankResponse.value.data.data.length > 0) {

                newCompletedSteps[0] = true;
                newExistingData.bankDetails = bankResponse.value.data.data[0]; // Take first bank account
            } else {
                console.log('No bank details found or invalid response structure');
            }

            // Check payment details - based on the response structure you provided
            if (paymentResponse.status === 'fulfilled' &&
                paymentResponse.value?.success &&
                paymentResponse.value?.data &&
                Array.isArray(paymentResponse.value.data) &&
                paymentResponse.value.data.length > 0) {

                // Mark step 0 as completed if either bank OR payment details exist
                newCompletedSteps[0] = true; // Set to true regardless of bank details
                newExistingData.paymentDetails = paymentResponse.value.data[0]; // Take first payment detail
            } else {
                console.log('No payment details found or invalid response structure');
            }

            // Check biller address - handle nested data structure
            if (billerResponse.status === 'fulfilled' &&
                billerResponse.value?.success &&
                billerResponse.value?.data?.success &&
                billerResponse.value?.data?.data &&
                Array.isArray(billerResponse.value.data.data) &&
                billerResponse.value.data.data.length > 0) {

                newCompletedSteps[1] = true;
                newExistingData.billerAddress = billerResponse.value.data.data[0]; // Take first address
            } else {
                console.log('No biller address found or invalid response structure');
            }

            // Check currency - handle nested data structure
            if (currencyResponse.status === 'fulfilled' &&
                currencyResponse.value?.success &&
                currencyResponse.value?.data?.success &&
                currencyResponse.value?.data?.data &&
                Array.isArray(currencyResponse.value.data.data) &&
                currencyResponse.value.data.data.length > 0) {

                newCompletedSteps[2] = true;
                newExistingData.currency = currencyResponse.value.data.data[0]; // Take first currency
            } else {
                console.log('No currency found or invalid response structure');
            }

            setCompletedSteps(newCompletedSteps);
            setExistingData(newExistingData);

            // Load skipped steps from localStorage
            const savedProgress = localStorage.getItem('onboarding_progress');
            if (savedProgress) {
                try {
                    const parsed = JSON.parse(savedProgress);
                    if (parsed.skippedSteps && parsed.facilityId === facilityId) {
                        setSkippedSteps(parsed.skippedSteps);
                    }
                } catch (e) {
                    console.error('Error parsing saved progress:', e);
                }
            }

            // Save current progress to localStorage (only skip status, not completion)
            const progressData = {
                skippedSteps: skippedSteps,
                lastChecked: Date.now(),
                facilityId: facilityId
            };
            localStorage.setItem('onboarding_progress', JSON.stringify(progressData));

        } catch (error) {
            console.error('Error checking existing data:', error);

            // Fallback to localStorage only for skipped steps, not completion status
            const savedProgress = localStorage.getItem('onboarding_progress');
            if (savedProgress) {
                try {
                    const { skippedSteps: saved, lastChecked, facilityId: savedFacilityId } = JSON.parse(savedProgress);
                    const currentFacilityId = await getItem('selectedFacilityId');

                    // Only use localStorage data if it's for the same facility and less than 1 hour old
                    if (savedFacilityId === currentFacilityId &&
                        Date.now() - lastChecked < 60 * 60 * 1000) {
                        if (saved) setSkippedSteps(saved);
                    }
                } catch (e) {
                    console.error('Error parsing saved progress:', e);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const allStepsCompleted = completedSteps.every(step => step === true);

    const handleFinish = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${finishOnboardingURL}/${facilityId}`, 'POST');

            if (response.success) {
                toastify('Onboarding completed successfully! Welcome', 'success');
                navigate('/facility');
            }
        } catch (error) {
            toastify('Failed to complete onboarding', 'error');
        }
    }

    const handleNext = () => {
        if (activeTab < steps.length - 1) {
            setActiveTab(activeTab + 1);
        }
    };

    const handlePrevious = () => {
        if (activeTab > 0) {
            setActiveTab(activeTab - 1);
        }
    };

    const handleStepComplete = async (stepIndex) => {
        setCompletedSteps(prev => {
            const newSteps = [...prev];
            newSteps[stepIndex] = true;
            return newSteps;
        });

        // Remove from skipped if it was previously skipped
        setSkippedSteps(prev => {
            const newSkipped = [...prev];
            newSkipped[stepIndex] = false;
            return newSkipped;
        });

        // Re-check data from API to ensure we have the latest state
        await checkExistingData();
    };

    const isOnboardingComplete = () => {
        return completedSteps.every(step => step);
    };

    const getCompletionPercentage = () => {
        const completed = completedSteps.filter(step => step).length;
        return Math.round((completed / completedSteps.length) * 100);
    };

    const getStepStatus = (index) => {
        if (completedSteps[index]) return 'completed';
        if (skippedSteps[index]) return 'skipped';
        return 'pending';
    };

    const renderActiveTab = () => {
        if (loading) {
            return (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2">Checking your progress...</div>
                </div>
            );
        }

        switch (activeTab) {
            case 0:
                return (
                    <BankDetailsTab
                        key={`bank-tab-${existingData.paymentDetails?._id || 'no-payment'}-${existingData.bankDetails?._id || 'no-bank'}`}
                        onComplete={() => handleStepComplete(0)}
                        isCompleted={completedSteps[0]}
                        existingData={existingData}
                    />
                );
            case 1:
                return (
                    <BillerAddressTab
                        onComplete={() => handleStepComplete(1)}
                        isCompleted={completedSteps[1]}
                        existingData={existingData.billerAddress}
                    />
                );
            case 2:
                return (
                    <CurrencyTab
                        onComplete={() => handleStepComplete(2)}
                        isCompleted={completedSteps[2]}
                        existingData={existingData.currency}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="container-fluid min-vh-100 bg-light">
            <div className="row justify-content-center">
                <div className="col-11">
                    <div className="card shadow-lg border-0 mt-4">
                        <div className="card-header text-center py-4">
                            <h2 className="mb-2">Welcome! Let's Get Started</h2>
                            <p className="mb-0">Complete your onboarding process</p>
                        </div>

                        {/* Completion Status Card */}
                        {!loading && isOnboardingComplete() && (
                            <div className="alert alert-success mt-3 m-3" role="alert">
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-check-circle-fill me-2"></i>
                                    <div>
                                        <strong>Congratulations!</strong> Your onboarding is complete.
                                        You can now start using all features of the platform.
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Progress Alert */}
                        {!loading && !isOnboardingComplete() && (
                            <div className="alert alert-warning alert-dismissible m-3 mb-0" role="alert">
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <div className="flex-grow-1">
                                        <strong>Onboarding Incomplete</strong>
                                        <div className="mt-1">
                                            Please complete all steps to finish your setup ({getCompletionPercentage()}% complete)
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Progress Bar */}
                        {!loading && (
                            <div className="px-3 pt-3">
                                <div className="progress" style={{ height: '8px' }}>
                                    <div
                                        className="progress-bar bg-success"
                                        role="progressbar"
                                        style={{ width: `${getCompletionPercentage()}%` }}
                                    ></div>
                                </div>
                            </div>
                        )}

                        {/* Navigation Tabs */}
                        <div className="card-body p-0">
                            <ul className="nav nav-tabs nav-fill border-0" role="tablist">
                                {steps.map((step, index) => {
                                    const status = getStepStatus(index);
                                    return (
                                        <li className="nav-item" key={step.id}>
                                            <button
                                                className={`nav-link ${activeTab === index ? 'active' : ''} 
                                                        ${status === 'completed' ? 'text-success' : ''} 
                                                        ${status === 'skipped' ? 'text-warning' : ''}`}
                                                onClick={() => setActiveTab(index)}
                                                type="button"
                                                disabled={loading}
                                            >
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <span className="me-2">{step.icon}</span>
                                                    <div>
                                                        <div>{step.title}</div>
                                                        {status === 'completed' && (
                                                            <small className="text-success">✓ Complete</small>
                                                        )}
                                                        {status === 'skipped' && (
                                                            <small className="text-warning">⚠ Skipped</small>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>

                            {/* Tab Content */}
                            <div className="tab-content p-4">
                                {renderActiveTab()}
                            </div>

                            {/* Navigation Buttons */}
                            {!loading && (
                                <div className="card-footer bg-light border-top-0">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={handlePrevious}
                                            disabled={activeTab === 0}
                                        >
                                            <i className="bi bi-arrow-left me-1"></i>
                                            Previous
                                        </button>

                                        <div className="d-flex gap-2">
                                            <div className="actions">
                                                {activeTab < steps.length - 1 ? (
                                                    <Button
                                                        onClick={handleNext}
                                                        style={{ borderRadius: "12px" }}
                                                    >
                                                        Next
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        onClick={handleFinish}
                                                        style={{ borderRadius: "12px" }}
                                                        disabled={!allStepsCompleted} // 🔒 only enable when all required data exists
                                                    >
                                                        {allStepsCompleted ? "Finish" : "Complete all steps to finish"}
                                                    </Button>
                                                )}
                                            </div>



                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Onboarding;